import React, { useContext } from 'react'
import { Button } from '@sainsburys-tech/bolt'
import { ThemeContext } from 'styled-components'
import {
  StyledItemContainer,
  StyledItemCarousel,
  StyledReadMore,
  StyledExpandedItemImage,
  StyledExpandedItemHeadingContainer,
  StyledCloseButton,
  StyledItemBodyCopy,
  StyledExpandedItemBodyCopy,
  StyledCtaAndImageWrapper
} from './editorial.styles'
import Image from '../../../helpers/Image/Image'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolDataAttributes } from '../../../helpers/tracking'
import { useWindowDimensions } from '../../../helpers/hooks'
import { EditorialProps, ItemType } from './types'

const Editorial = ({
  items,
  showItems,
  selectedProductHeading,
  setShowLook,
  changeSelectedProductId,
  onLinkClick
}: EditorialProps): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const { channel } = theme
  const isSainsburys = channel === 'sainsburys'
  const mobileLayoutBreakpoint = 575.98
  const { screenWidth } = useWindowDimensions()
  const itemCharLimit = screenWidth < mobileLayoutBreakpoint ? 200 : screenWidth / 2.5
  const selectedProduct = items.find((element) => element.heading === selectedProductHeading)

  const getGOLDataAttributes = (linkTitle, linkUrl, action?: string) =>
    createGolDataAttributes('isolated', 'M038 - NA', linkTitle, linkUrl, undefined, action)

  const addGolTracking = (event, linkTitle, linkUrl) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, linkUrl, linkTitle) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(linkUrl))
    }
    addGolTracking(event, linkTitle, linkUrl)
  }

  const handleOnClick = (product) => {
    const productClicked = product === null
    setShowLook(productClicked)
    changeSelectedProductId(product)
  }

  const createItem = (item: ItemType, index: number) => {
    const hasImage = item?.image
    const noImageCharLimit = screenWidth < mobileLayoutBreakpoint ? itemCharLimit : screenWidth / 1.75
    const trimmedBodyCopy = item.bodyCopy.substring(0, hasImage ? itemCharLimit : noImageCharLimit)
    const showReadMore = item.bodyCopy.length > trimmedBodyCopy.length
    const bodyCopy = `${trimmedBodyCopy}${showReadMore ? '...' : ''}`

    return (
      <StyledItemContainer key={`item-${index}`} data-test={`editorial-item-container-${index}`}>
        {hasImage && (
          <Image
            src={item.image.url}
            alt={item.image.altText}
            responsive
            data-test={`editorial-item-image-${index}`}
            style={{ aspectRatio: '2/1' }}
          />
        )}
        <h3 data-test={`editorial-item-heading-${index}`}>{item.heading}</h3>
        <StyledItemBodyCopy data-test={`editorial-item-bodycopy-${index}`} isSainsburys={isSainsburys}>
          {bodyCopy}
        </StyledItemBodyCopy>
        {showReadMore && (
          <StyledReadMore
            data-test={`editorial-item-read-more-${index}`}
            onClick={() => {
              handleOnClick(item.heading)
            }}
          >
            Read more
          </StyledReadMore>
        )}
        {item?.link && (
          <Button
            data-test={`editorial-item-cta-${index}`}
            role='link'
            title={item.link?.title}
            kind={item.link.style}
            href={item.link.url}
            target={item.link.target}
            as='a'
            onClick={(evt) => ctaClick(evt, item.link.url, item.link.title)}
            style={{ marginTop: 'auto', width: item.link.style === 'link' ? 'fit-content' : '100%' }}
          >
            {item.link.copy}
          </Button>
        )}
      </StyledItemContainer>
    )
  }

  const createExpandedItem = (item: ItemType) => {
    const hasImage = !!item?.image
    return (
      <>
        <StyledExpandedItemHeadingContainer>
          <h3 data-test='editorial-expanded-item-heading'>{item?.heading}</h3>
          <StyledCloseButton
            data-test='editorial-expanded-item-close'
            onClick={() => {
              handleOnClick(null)
            }}
          />
        </StyledExpandedItemHeadingContainer>
        <StyledCtaAndImageWrapper hasImage={hasImage}>
          <StyledExpandedItemBodyCopy data-test='editorial-expanded-item-bodycopy' hasImage={hasImage}>
            {item?.bodyCopy}
          </StyledExpandedItemBodyCopy>
          {item?.link && (
            <Button
              data-test='editorial-expanded-item-cta'
              role='link'
              title={item.link?.title}
              kind='link'
              href={item.link.url}
              target={item.link.target}
              as='a'
              onClick={(evt) => ctaClick(evt, item.link.url, item.link.title)}
              style={{ width: 'fit-content' }}
            >
              {item.link.copy}
            </Button>
          )}
          {hasImage && (
            <StyledExpandedItemImage
              key={item?.image.url}
              src={item?.image.url}
              alt={item?.image.altText}
              responsive
              data-test='editorial-expanded-item-image'
              smart
            />
          )}
        </StyledCtaAndImageWrapper>
      </>
    )
  }

  return (
    <>
      {showItems ? (
        <StyledItemCarousel data-test='editorial-carousel'>
          {items.map((item, index) => createItem(item, index))}
        </StyledItemCarousel>
      ) : (
        createExpandedItem(selectedProduct)
      )}
    </>
  )
}

export default Editorial
